import React, { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import ReactDOM from 'react-dom/client';
import SharedSellerReport from './pages/SharedSellerReport';
import { isPublicSellerPage, isPublicSellerRequest, publicReportOptions } from './services/publicSellerReport';

class GlobalErrorBoundary extends Component<{ children: ReactNode; publicMode?: boolean }, { error: Error | null }> {
  constructor(props: { children: ReactNode; publicMode?: boolean }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.publicMode) return;
    console.error('[GlobalErrorBoundary] Caught render error:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.publicMode) return <main className="min-h-screen bg-white px-5 py-20 text-center"><h1 className="text-xl font-semibold">Report unavailable</h1><p className="mt-3 text-sm text-slate-500">Please check the report link.</p></main>;
      return (
        <div style={{ fontFamily: 'monospace', padding: '2rem', background: '#fff1f2', minHeight: '100vh' }}>
          <h2 style={{ color: '#be123c', marginBottom: '1rem' }}>Something went wrong</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#1e293b', fontSize: '0.85rem' }}>
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
          <button
            style={{ marginTop: '1.5rem', padding: '0.5rem 1rem', background: '#be123c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import App from './App';
import './styles/index.css';
import { AuthProvider, broadcastAccessControlUpdate } from './contexts/AuthContext';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim().replace(/\/$/, '') ?? '';
const RESOLVED_API_BASE_URL = API_BASE_URL;
const TOKEN_STORAGE_KEY = 'kwsa_auth_token';
const ACTIVE_CONTEXT_KEY = 'kwsa_active_context_id';
const AUTH_EXPIRED_EVENT = 'kwsa-auth-expired';
const publicMode = isPublicSellerPage(window.location.pathname);
if (!publicMode) void import('./styles/internal-fonts.css');
if (publicMode) {
  const referrer = document.createElement('meta'); referrer.name = 'referrer'; referrer.content = 'no-referrer'; document.head.appendChild(referrer);
  const robots = document.createElement('meta'); robots.name = 'robots'; robots.content = 'noindex,nofollow,noarchive'; document.head.appendChild(robots);
  const csp = document.createElement('meta'); csp.httpEquiv = 'Content-Security-Policy';
  const apiOrigin = RESOLVED_API_BASE_URL ? new URL(RESOLVED_API_BASE_URL, window.location.origin).origin : window.location.origin;
  csp.content = `default-src 'self'; script-src 'self'; style-src 'self' 'unsafe-inline'; img-src 'self' blob: data: ${apiOrigin}; font-src 'self'; connect-src 'self' ${apiOrigin}; object-src 'none'; base-uri 'none'; form-action 'none'`;
  document.head.appendChild(csp);
}

function extractSuspensionReason(message: string): string | null {
  const match = message.match(/reason:\s*(.+)$/i);
  return match?.[1]?.trim() || null;
}

async function handleSuspensionResponse(response: Response): Promise<void> {
  if (response.status !== 403) return;

  const body = await response.clone().json().catch(async () => {
    const text = await response.clone().text().catch(() => '');
    return text ? { error: text } : {};
  }) as { error?: string };

  const message = String(body.error ?? '').trim();
  if (!/temporarily suspended/i.test(message)) return;

  broadcastAccessControlUpdate({
    featureEnabled: true,
    isTemporarilySuspended: true,
    suspendedReason: extractSuspensionReason(message),
  });
}

function shouldHandleAuthFailure(url: string): boolean {
  if (!url.includes('/api/')) return false;
  // Only force logout when explicit identity endpoints say the token is invalid.
  // Other routes may return 401 during transient backend/DB instability.
  return url.includes('/api/auth/me') || url.includes('/api/auth/contexts');
}

function handleAuthFailure(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(ACTIVE_CONTEXT_KEY);
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  if (isPublicSellerRequest(url, window.location.origin)) {
    const resolvedUrl = url.startsWith('/api/') && RESOLVED_API_BASE_URL ? `${RESOLVED_API_BASE_URL}${url}` : url;
    const publicInput = input instanceof Request ? new Request(resolvedUrl, input) : resolvedUrl;
    return originalFetch(publicInput, publicReportOptions(init, input instanceof Request ? input : undefined));
  }

  const isApi = url.startsWith('/api/') || url === '/api';
  const isUploads = url.startsWith('/uploads/') || url === '/uploads';

  if (isApi || isUploads) {
    // Rewrite to absolute URL when a remote API base is configured (production)
    const resolvedUrl = RESOLVED_API_BASE_URL ? `${RESOLVED_API_BASE_URL}${url}` : url;

    // Inject auth token for API calls
    let resolvedInit = init;
    if (isApi) {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const activeContextId = localStorage.getItem('kwsa_active_context_id') ?? '';
      // Don't overwrite an Authorization header the caller already set
      const existingAuth = (init?.headers as Record<string, string> | undefined)?.['Authorization']
        ?? (init?.headers as Record<string, string> | undefined)?.['authorization'];
      if (token && !existingAuth) {
        resolvedInit = {
          ...init,
          headers: {
            ...(init?.headers ?? {}),
            Authorization: `Bearer ${token}`,
            'X-Active-Context': activeContextId,
          },
        };
      }
    }

    if (typeof input === 'string' || input instanceof URL) {
      const response = await originalFetch(resolvedUrl, resolvedInit);
      if (response.status === 401 && shouldHandleAuthFailure(resolvedUrl)) {
        handleAuthFailure();
      }
      await handleSuspensionResponse(response);
      return response;
    }
    const response = await originalFetch(new Request(resolvedUrl, input), resolvedInit);
    if (response.status === 401 && shouldHandleAuthFailure(resolvedUrl)) {
      handleAuthFailure();
    }
    await handleSuspensionResponse(response);
    return response;
  }

  return originalFetch(input, init);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  publicMode ? (
    <GlobalErrorBoundary publicMode>
      <SharedSellerReport />
    </GlobalErrorBoundary>
  ) : (
    <React.StrictMode>
      <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
      </GlobalErrorBoundary>
    </React.StrictMode>
  ),
);
